import { createApi } from "@reduxjs/toolkit/query/react";
import { IAddressData } from "@src/interface/Address";
import { setAddresses } from "@src/store/reducers/AddressSlice";
import { baseQueryFn } from "./baseQuery";
import { createAuthEndpoints } from "./endpoints/auth.endpoints";
import { createCartEndpoint } from "./endpoints/cart.endpoints";
import { createCustomerEndpoints } from "./endpoints/customer.endpoints";
import { createMenuEndpoints } from "./endpoints/menu.endpoints";
import { createProductEndpoints } from "./endpoints/product.endpoint";
import { createSetupEndpoints } from "./endpoints/setup.endpoints";
import { createStoreEndpoints } from "./endpoints/store.endpoints";

/**
 * API RTK « critique » (listing, panier, auth, client, produits, menu, setup…).
 * Même `reducerPath` qu’avant : `rollingStartApi` (état / devtools inchangés pour cette partie).
 */
export const rolligStartApi = createApi({
  reducerPath: "rollingStartApi",
  baseQuery: baseQueryFn,
  endpoints: (builder) => ({
    ...createCartEndpoint(builder),
    ...createStoreEndpoints(builder),
    ...createCustomerEndpoints(builder),
    ...createSetupEndpoints(builder),
    ...createProductEndpoints(builder),
    ...createAuthEndpoints(builder),
    ...createMenuEndpoints(builder),
    postJobWidget: builder.query<
      {
        connectionStatus?: boolean;
        created?: boolean;
        field_missing?: boolean;
      },
      {
        body: {
          lastname: string;
          firstname: string;
          address: string;
          city: string;
          email: string;
          id: string;
          phone: string;
          message: string;
          brand: string;
        };
      }
    >({
      query: ({ body }) => ({
        url: "/jobwidget",
        method: "POST",
        body,
      }),
    }),

    getAddresses: builder.query<IAddressData[], void>({
      query: () => ({
        url: "/address",
        method: "GET",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled;
          dispatch(setAddresses(res.data));
        } catch (error) {}
      },
    }),
  }),
});

export const {
  useLazyPostSigninQuery,
  useLazyGetCurrentCustomerQuery,
  useGetCurrentCustomerQuery,
  useLazyGetProductByIdQuery,
  useLazyPostJobWidgetQuery,
  useLazySendSponsorshipByEmailQuery,
  useGetCartQuery,
  useLazyGetCartQuery,
  usePostCartDeliveryOptionsMutation,
  useLazyGetAddressesQuery,
  useLazyGetStoreAvisVerifieQuery,
  usePostLikeProductMutation,
  useLazyGetSetupPrivateQuery,
  useLazyGetSetupV2Query,
  useAddProductToCurrentCartMutation,
  usePostSignoutMutation,
  useDeleteProductFromCurrentCartMutation,
  useAddDiscountMutation,
  useRemoveDiscountMutation,
  usePostDiscountFreeProductMutation,
  useLazyGetMenuCollectionQuery,
  usePostRegisterNewsLetterMutation,
  useLazyPostSignInFacebookQuery,
  useLazyPostSignInGoogleQuery,
  useLazyPostNewRegisterNewsLetterQuery,
  useLazyPostRegisterQuery,
  useLazyPostCartReorderQuery,
  useLazyGetStockoutProductQuery,
  useLazyGetStoreItemQuery,
  useLazyPostCheckEmailQuery,
  useLazyGetHappyHourQuery,
} = rolligStartApi;
